import { Divider, List, ListItem, ListItemIcon, ListItemText, Stack, Switch, Typography, Box, IconButton } from "@mui/material";
import { useCallback, useState } from "react";
import { Edit, Logout, Notifications, Webhook } from "@mui/icons-material";
import { useAppFwContext } from "../../../../appfw/AppFwContextContextProvider.tsx";
import { useLoadingControl } from "../../../../appfw/loading/use-loading-control.tsx";
import useNotifications from "../../../../appfw/notifications/use-notifications";
import { useNavigate, useLocation } from "react-router-dom";
import { W2d2dNavigation } from "../../../W2d2dNavigation.tsx";
import Session from "../../../../appfw/authentication/Session";
import { useSession } from "@toolpad/core";
import DialogActionButton from "../../../../appfw/ui/dialogs/DialogActionButton.tsx";
import { w2d2dApiClient } from "../../../w2d2d-api-client.ts";
import AvatarWidget from "../../components/users/AvatarWidget.tsx";
import { TextInputDialog } from "../../../../appfw/ui/dialogs/TextInputDialog.tsx";

export default function SettingsPage() {

    const loadingControl = useLoadingControl("settings")

    const navigate = useNavigate()
    const location = useLocation()

    const session = useSession<Session>()
    const notifications = useNotifications()

    const handleNotificationsSwitchChange = useCallback(async () => {
        await loadingControl.withLoading(async () => {
            if (notifications.pushEnabled && Notification.permission === 'granted') {
                await notifications.unsubscribe(true)
            } else {
                await notifications.subscribe()
            }
        })
        console.log("[notifications] notifications switch done", notifications.pushEnabled)
    }, [notifications, loadingControl])

    const handleLogoutClick = useCallback(async () => {
        session?.logout()
    }, [session])

    const userId = session?.user?.id

    const appFwContext = useAppFwContext()
    const [editNameDialogOpen, setEditNameDialogOpen] = useState(false)

    const handleEditNameClick = useCallback(() => {
        setEditNameDialogOpen(true)
    }, [])

    const handleEditNameDialogClose = useCallback(async (newName: string | null) => {
        if (newName !== null && userId) {
            await loadingControl.withLoading(async () => {
                await appFwContext.appFwApiClient.updateUserName({ userId, name: newName })
                await session?.refresh()
            })
        }
        setEditNameDialogOpen(false)
    }, [userId, loadingControl, appFwContext, session])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', flexGrow: 1 }}>
            <Stack direction="column" alignItems="center" sx={{ padding: 2, marginTop: "16px" }}>
                {(userId && <AvatarWidget id={userId} size={64} />)}
                <Stack direction="row" alignItems="center" gap={1} sx={{ marginTop: "12px" }}>
                    <Typography variant="h6">
                        {session?.user.name}
                    </Typography>
                    <IconButton size="small" onClick={handleEditNameClick}>
                        <Edit fontSize="small" />
                    </IconButton>
                </Stack>
                <Typography>
                    {session?.user.email}
                </Typography>
            </Stack>

            <Typography color="text.secondary" variant="body2" sx={{ marginBottom: 1, textAlign: "center" }}>
                App-Version: {process.env.VITE_BETA === 'true' ? 'Beta ' : ''}{__APP_VERSION__}
            </Typography>

            <List sx={{ flexGrow: 1, paddingLeft: 2, paddingRight: 2 }}>
                <Divider />

                <ListItem sx={{ marginTop: 2, paddingLeft: 2, paddingRight: 2 }}>
                    <ListItemIcon>
                        <Notifications />
                    </ListItemIcon>
                    <ListItemText
                        primary="Benachrichtigungen"
                    // secondary="Erlaube uns, Dich über wichtige Ereignisse zu informieren"
                    >
                    </ListItemText>
                    <Switch
                        disabled={loadingControl.isLoading || notifications.isInitializing || notifications.permission === 'denied'}
                        checked={notifications.pushEnabled == true}
                        onChange={handleNotificationsSwitchChange}
                    >
                    </Switch>
                </ListItem>
                {notifications.permission === 'denied' && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ paddingLeft: 9, paddingRight: 2, display: 'block', marginTop: -1, marginBottom: 1 }}
                    >
                        Benachrichtigungen sind blockiert,<br />
                        bitte in den Einstellungen aktivieren.
                    </Typography>
                )}

                <ListItem
                    onClick={() => W2d2dNavigation.settings.pods.navigate(navigate, location)}
                    sx={{ cursor: "pointer", paddingLeft: 2, paddingRight: 2 }}
                >
                    <ListItemIcon>
                        <Webhook />
                    </ListItemIcon>
                    <ListItemText
                        primary="Veranstaltungsquellen verwalten"
                    />
                </ListItem>

                <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

                <ListItem onClick={handleLogoutClick} sx={{ cursor: "pointer", paddingLeft: 2, paddingRight: 2 }}>
                    <ListItemIcon>
                        <Logout />
                    </ListItemIcon>
                    <ListItemText primary="Abmelden" />
                </ListItem>
            </List>

            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, mt: 'auto' }}>
                <DialogActionButton
                    outlet={
                        <Typography color={"warning"} sx={{ cursor: 'pointer' }}>
                            Account löschen
                        </Typography>
                    }
                    dialogTitle={"Account löschen"}
                    dialogContent={"Möchtest Du wirklich Deinen Account und alle damit verbundenen Daten löschen?"}
                    dialogActions={[
                        { label: "Nein" },
                        {
                            label: "Ja",
                            action: async () => {
                                await w2d2dApiClient.deleteAccount()
                                handleLogoutClick()
                                return Promise.resolve()
                            }
                        },
                    ]}
                />
            </Box>

            <TextInputDialog
                open={editNameDialogOpen}
                dialogTitle="Name ändern"
                inputLabel="Name"
                validate={(value) => {
                    const trimmed = value.trim()
                    const current = (session?.user?.name || session?.user?.oidcName || "").trim()
                    return trimmed.length > 0 && trimmed !== current
                }}
                initialValue={session?.user?.name || session?.user?.oidcName || ""}
                onClose={handleEditNameDialogClose}
                confirmText="Speichern"
            />
        </Box>
    )
}
